/**
 * App Info: which version is running, and what changed in it.
 *
 * The version and the build number are shown as two separate things because
 * they are two separate things. The version says what changed for you; the
 * build number counts builds and moves even when nothing changed. They used to
 * be the same number, which is why every release read 1.0.<build>.
 *
 * The change list is generated from the commit history by
 * scripts/release/build-changelog.mjs, which drops anything describing the
 * plumbing. What is left is meant to be readable by someone who does not know
 * how the app is built - which is everyone using it.
 */

import { useQuery } from "@tanstack/react-query";
import {
  ArrowLeft,
  Bug,
  Calendar,
  Hash,
  Info,
  Sparkles,
  Wrench,
} from "lucide-react";
import { useLocaleFormat } from "@/hooks/useLocaleFormat";
import React from "react";
import { useNavigate } from "react-router-dom";

interface BuildInfo {
  buildNumber: number;
  buildDate: string;
  gitCommit: string;
  version: string;
  environment: string;
}

interface Release {
  version: string;
  date: string | null;
  features: string[];
  improvements: string[];
  fixes: string[];
}

async function loadJson<T>(path: string): Promise<T> {
  const response = await fetch(path);
  if (!response.ok) throw new Error(`${path} returned ${response.status}`);
  return response.json() as Promise<T>;
}

const SECTIONS = [
  {
    key: "features",
    label: "New features",
    Icon: Sparkles,
    tone: "text-sky-600",
  },
  {
    key: "improvements",
    label: "Improvements",
    Icon: Wrench,
    tone: "text-emerald-600",
  },
  { key: "fixes", label: "Fixes", Icon: Bug, tone: "text-amber-600" },
] as const;

export const AppInfoPage: React.FC = () => {
  const navigate = useNavigate();
  const fmt = useLocaleFormat();
  const formatDate = (value: string | null) =>
    value ? fmt.date(value, "long") : "—";

  const build = useQuery({
    queryKey: ["app", "build-info"],
    queryFn: () => loadJson<BuildInfo>("/build-info.json"),
  });

  const changelog = useQuery({
    queryKey: ["app", "changelog"],
    queryFn: () => loadJson<{ releases: Release[] }>("/changelog.json"),
  });

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-6"
      >
        <ArrowLeft className="w-4 h-4" />
        Back
      </button>

      <h1 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
        <Info className="w-6 h-6 text-sky-600" />
        App Info
      </h1>

      <div
        className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 mb-8"
        data-testid="build-info"
      >
        {build.isLoading && (
          <p className="text-sm text-gray-600">Reading version details...</p>
        )}

        {build.isError && (
          <p className="text-sm text-amber-700" data-testid="build-info-error">
            Version details are unavailable.
          </p>
        )}

        {build.data && (
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <div>
              <dt className="text-gray-500 flex items-center gap-1.5">
                <Hash className="w-4 h-4" />
                Version
              </dt>
              <dd
                className="font-mono text-lg text-gray-900"
                data-testid="app-version"
              >
                {build.data.version}
              </dd>
            </div>
            <div>
              <dt className="text-gray-500 flex items-center gap-1.5">
                <Hash className="w-4 h-4" />
                Build
              </dt>
              <dd
                className="font-mono text-lg text-gray-900"
                data-testid="build-number"
              >
                #{build.data.buildNumber}
              </dd>
            </div>
            <div>
              <dt className="text-gray-500 flex items-center gap-1.5">
                <Calendar className="w-4 h-4" />
                Built
              </dt>
              <dd className="text-gray-900">
                {formatDate(build.data.buildDate)}
              </dd>
            </div>
            <div>
              <dt className="text-gray-500">Environment</dt>
              <dd className="text-gray-900">{build.data.environment}</dd>
            </div>
          </dl>
        )}
      </div>

      <h2 className="text-xl font-bold text-gray-900 mb-4">
        What&apos;s changed
      </h2>

      {changelog.isLoading && (
        <p className="text-sm text-gray-600">Loading the change list...</p>
      )}

      {changelog.isError && (
        <p className="text-sm text-amber-700" data-testid="changelog-error">
          The change list could not be loaded.
        </p>
      )}

      {changelog.data?.releases.length === 0 && (
        <p className="text-sm text-gray-600">No releases recorded yet.</p>
      )}

      <div className="space-y-8">
        {changelog.data?.releases.map((release) => (
          <section
            key={release.version}
            className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
            data-testid={`release-${release.version}`}
          >
            <div className="flex items-baseline justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">
                Version {release.version}
              </h3>
              <span className="text-sm text-gray-500">
                {formatDate(release.date)}
              </span>
            </div>

            {SECTIONS.every((s) => release[s.key].length === 0) && (
              <p className="text-sm text-gray-600">
                Nothing in this release changed how the app behaves.
              </p>
            )}

            {SECTIONS.map(({ key, label, Icon, tone }) =>
              release[key].length === 0 ? null : (
                <div key={key} className="mb-4 last:mb-0">
                  <h4 className="text-sm font-semibold text-gray-800 flex items-center gap-1.5 mb-2">
                    <Icon className={`w-4 h-4 ${tone}`} />
                    {label}
                  </h4>
                  <ul className="list-disc pl-6 space-y-1">
                    {release[key].map((line) => (
                      <li key={line} className="text-sm text-gray-700">
                        {line}
                      </li>
                    ))}
                  </ul>
                </div>
              ),
            )}
          </section>
        ))}
      </div>
    </div>
  );
};

export default AppInfoPage;
